/**
 * /{ws}/contacts — the Contacts area: everyone the workspace mails with (core ContactsViewModel), filtered All / Email /
 * Clients / Team (the Contacts sidebar's rows and Add contact; the screen shows its own while that sidebar doesn't) and
 * searched by name, address or company. A row's menu marks it Client / Team / other (their mail follows to that
 * tab), sends them an email or removes them; "Add contact" adds one by hand. Selecting a contact (`?contact=`, a
 * people card's "Open contact") opens its detail beside the list: type, projects, recent emails (People cards design).
 */
import type { ContactKind, MailContact } from "@bundle/contracts";
import { Shell, type ContactsViewModel } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import { Avatar, Button, Chip, ComingSoon, Divider, IconButton, Menu, Pressable, ProjectPicker, ScrollView, SegmentedControl, Select, Sheet, Spinner, Stack, Text, TextField, mailShortDate, openComposer, projectLabel, useFeatureAccess, useLocale, useMail, useNavigation, usePaneWidthAtLeast, useT, useTr, useViewModel, useWorkspace, type ChipTone, type MenuEntry } from "@bundle/ui";
import { useEffect, useState } from "react";
import { onNewContactRequest } from "./contactIntents.ts";

type Filter = ContactKind | "all";
const KIND_LABEL: Record<ContactKind, "mail.contacts.filter.email" | "mail.contacts.filter.client" | "mail.contacts.filter.team"> = {
  email: "mail.contacts.filter.email",
  client: "mail.contacts.filter.client",
  team: "mail.contacts.filter.team",
};
const KIND_TONE: Record<ContactKind, ChipTone> = { email: "neutral", client: "tag.blue", team: "tag.purple" };

export function ContactsScreen({ kind, contactId, filterInSidebar = false }: { kind?: ContactKind | undefined; contactId?: string | undefined; filterInSidebar?: boolean }) {
  const t = useT();
  const mail = useMail();
  if (mail === undefined) return <ComingSoon icon="users" title={t("mail.contacts.title")} body={t("mail.contacts.empty")} />;
  return <ContactsView vm={mail.contacts} kind={kind} contactId={contactId} mailboxId={mail.mailbox.state.mailboxId} filterInSidebar={filterInSidebar} />;
}

function ContactsView({ vm, kind, contactId, mailboxId, filterInSidebar }: { vm: ContactsViewModel; kind: ContactKind | undefined; contactId: string | undefined; mailboxId: string | undefined; filterInSidebar: boolean }) {
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  const s = useViewModel(vm);
  const [adding, setAdding] = useState(false);
  // The sidebar's Add contact.
  useEffect(() => onNewContactRequest(() => setAdding(true)), []);
  useEffect(() => void vm.refresh(), [vm]);
  useEffect(() => vm.setKind(kind), [vm, kind]);
  useEffect(() => void vm.loadProjects(), [vm]);
  const wide = usePaneWidthAtLeast(900);
  const open = contactId === undefined ? undefined : s.all.find((c) => c.id === contactId);
  const url = (k: ContactKind | undefined, id: string | undefined) =>
    Shell.formatUrl({ type: "contacts", ws: ws.slug, ...(k === undefined ? {} : { kind: k }), ...(id === undefined ? {} : { contactId: id }) });
  const setFilter = (f: Filter) => nav.replace(url(f === "all" ? undefined : f, contactId));
  const select = (id: string | undefined) => nav.replace(url(kind, id));
  // A narrow pane shows the open contact alone (with a back button); a wide one beside the list.
  if (open !== undefined && !wide) {
    return <ContactDetail vm={vm} c={open} mailboxId={mailboxId} onClose={() => select(undefined)} />;
  }
  const filters: { value: Filter; label: string; count: number }[] = [
    { value: "all", label: t("mail.contacts.filter.all"), count: s.counts.all },
    { value: "email", label: t("mail.contacts.filter.email"), count: s.counts.email },
    { value: "client", label: t("mail.contacts.filter.client"), count: s.counts.client },
    { value: "team", label: t("mail.contacts.filter.team"), count: s.counts.team },
  ];
  return (
    <Stack grow direction="horizontal">
    <Stack grow testID="contacts-screen">
      <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="6" paddingY="4" wrap>
        <Text variant="title2" weight="bold">
          {t("mail.contacts.title")}
        </Text>
        {filterInSidebar ? null : <Button icon="plus" label={t("mail.contacts.add")} onPress={() => setAdding(true)} testID="contacts-add" />}
      </Stack>
      <Stack paddingX="6" gap="3">
        {filterInSidebar ? null : <SegmentedControl<Filter> options={filters} value={s.kind ?? "all"} onChange={setFilter} variant="pills" size="sm" accessibilityLabel={t("mail.contacts.kind")} testID="contacts-filter" />}
        <TextField value={s.query} onChange={(q) => vm.setQuery(q)} placeholder={t("mail.contacts.search")} accessibilityLabel={t("mail.contacts.search")} leadingIcon="search" testID="contacts-search" />
      </Stack>
      {s.status === "loading" ? (
        <Stack grow align="center" justify="center">
          <Spinner />
        </Stack>
      ) : s.status === "failed" ? (
        <Stack grow align="center" justify="center" gap="3">
          <Text color="text.secondary">{t("mail.loadFailed")}</Text>
          <Button variant="secondary" label={t("mail.retry")} onPress={() => void vm.refresh()} testID="contacts-retry" />
        </Stack>
      ) : s.contacts.length === 0 ? (
        <Stack grow align="center" justify="center" padding="8" testID="contacts-empty">
          <Text color="text.secondary" align="center">
            {t("mail.contacts.empty")}
          </Text>
        </Stack>
      ) : (
        <ScrollView testID="contacts-list">
          <Stack paddingY="2">
            {s.contacts.map((c) => (
              <ContactRow key={c.id} vm={vm} c={c} mailboxId={mailboxId} selected={c.id === contactId} onOpen={() => select(c.id)} />
            ))}
          </Stack>
        </ScrollView>
      )}
      <AddContactSheet key={adding ? "adding" : "closed"} vm={vm} open={adding} onClose={() => setAdding(false)} initialKind={s.kind ?? "email"} />
    </Stack>
    {open === undefined ? null : (
      <>
        <Divider />
        <Stack width="dialog">
          <ContactDetail vm={vm} c={open} mailboxId={mailboxId} onClose={() => select(undefined)} />
        </Stack>
      </>
    )}
    </Stack>
  );
}

/** One contact (People cards design): header and actions, type, projects, recent emails. */
function ContactDetail({ vm, c, mailboxId, onClose }: { vm: ContactsViewModel; c: MailContact; mailboxId: string | undefined; onClose: () => void }) {
  const t = useT();
  const ws = useWorkspace();
  const nav = useNavigation();
  const locale = useLocale();
  const s = useViewModel(vm);
  const canChange = useFeatureAccess()("mail") === "full";
  const name = c.name ?? c.email;
  useEffect(() => {
    if (mailboxId !== undefined) void vm.loadRecent(c.id, mailboxId);
  }, [vm, c.id, mailboxId]);
  const projects = Mail.contactProjects(c.id, s.projects);
  const recent = s.recent?.contactId === c.id ? s.recent : undefined;
  const meta = [c.org, t("mail.contacts.messages", { count: c.messageCount })].filter((x) => x !== undefined && x !== "").join(" · ");
  const openPage = (rowId: string) => nav.open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: rowId }));
  return (
    <ScrollView testID="contact-detail">
      <Stack gap="5" padding="6">
        <Stack direction="horizontal" align="start" gap="4">
          <Avatar name={name} src={Mail.mailAvatarPath(ws.vm.wsId, c.email)} size="avatar.xl" />
          <Stack grow gap="0.5">
            <Text variant="title3" weight="bold" selectable testID="contact-detail-name">
              {name}
            </Text>
            <Text variant="footnote" color="text.secondary" selectable>
              {c.email}
            </Text>
            <Text variant="footnote" color="text.tertiary">
              {meta}
            </Text>
          </Stack>
          <IconButton icon="close" accessibilityLabel={t("common.close")} onPress={onClose} testID="contact-detail-close" />
        </Stack>
        <Stack direction="horizontal" gap="2" wrap>
          {canChange && mailboxId !== undefined ? (
            <Button size="sm" variant="primary" icon="mail" label={t("mail.people.newEmail")} onPress={() => openComposer({ mailboxId, mode: "new", to: [{ address: c.email, ...(c.name === undefined ? {} : { name: c.name }) }], cc: [], subject: "", text: "" })} testID="contact-detail-new-email" />
          ) : null}
        </Stack>
        {c.userId === undefined ? (
          <Stack gap="2">
            <Text variant="caption" weight="semibold" color="text.secondary">
              {t("mail.contacts.kind")}
            </Text>
            {canChange ? (
              <SegmentedControl<ContactKind>
                size="sm"
                value={c.kind}
                onChange={(k) => vm.setContactKind(c.id, k)}
                accessibilityLabel={t("mail.contacts.kind")}
                options={(["email", "client", "team"] as const).map((k) => ({ value: k, label: t(k === "client" ? "mail.people.kind.client" : KIND_LABEL[k]) }))}
                testID="contact-detail-kind"
              />
            ) : (
              <Chip label={t(KIND_LABEL[c.kind])} tone={KIND_TONE[c.kind]} />
            )}
          </Stack>
        ) : null}
        <Stack gap="2">
          <Stack direction="horizontal" align="center" justify="between">
            <Text variant="caption" weight="semibold" color="text.secondary">
              {t("mail.people.projects")}
            </Text>
            {canChange ? <ProjectPicker contactName={name} contactId={c.id} projects={s.projects} loading={s.projectsStatus === "loading"} onToggle={(rowId, on) => vm.setProject(c.id, rowId, on)} testID="contact-detail-add-project" /> : null}
          </Stack>
          {projects.length === 0 ? (
            <Text variant="footnote" color="text.tertiary">
              {t("mail.people.noProjectsFor")}
            </Text>
          ) : (
            projects.map((p) => (
              <Pressable key={p.rowId} direction="horizontal" radius="sm" onPress={() => openPage(p.rowId)} testID={`contact-detail-project-${p.rowId}`}>
                <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
                  <Stack grow>
                    <Text variant="footnote" lines={1}>
                      {projectLabel(p, t("db.untitled"))}
                    </Text>
                  </Stack>
                  {p.status === undefined ? null : (
                    <Text variant="caption" color="text.tertiary">
                      {p.status.name}
                    </Text>
                  )}
                </Stack>
              </Pressable>
            ))
          )}
        </Stack>
        {mailboxId === undefined ? null : (
          <Stack gap="2">
            <Stack direction="horizontal" align="center" justify="between">
              <Text variant="caption" weight="semibold" color="text.secondary">
                {t("mail.people.recent")}
              </Text>
              <Button
                size="sm"
                variant="ghost"
                label={t("mail.people.seeAllInMail")}
                onPress={() => nav.open(Shell.formatUrl({ type: "mail", ws: ws.slug, q: Mail.mailWithSearch([c.email]) }))}
                testID="contact-detail-see-all"
              />
            </Stack>
            {recent === undefined || recent.status === "loading" ? (
              <Spinner />
            ) : recent.threads.length === 0 ? (
              <Text variant="footnote" color="text.tertiary">
                {t("mail.people.noRecent")}
              </Text>
            ) : (
              recent.threads.map((th) => (
                <Pressable key={th.id} direction="horizontal" radius="sm" onPress={() => nav.open(Shell.formatUrl({ type: "mailThread", ws: ws.slug, threadId: th.id }))} testID={`contact-detail-thread-${th.id}`}>
                  <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
                    <Stack grow>
                      <Text variant="footnote" lines={1}>
                        {th.subject === "" ? t("mail.noSubject") : th.subject}
                      </Text>
                    </Stack>
                    <Text variant="caption" color="text.tertiary">
                      {mailShortDate(th.lastAt, locale)}
                    </Text>
                  </Stack>
                </Pressable>
              ))
            )}
          </Stack>
        )}
      </Stack>
    </ScrollView>
  );
}

function ContactRow({ vm, c, mailboxId, selected, onOpen }: { vm: ContactsViewModel; c: MailContact; mailboxId: string | undefined; selected: boolean; onOpen: () => void }) {
  const ws = useWorkspace();
  const t = useT();
  const name = c.name ?? c.email;
  // Everything in the menu changes something: none of it while Email is look-only (Settings → Features).
  const canChange = useFeatureAccess()("mail") === "full";
  const menu: MenuEntry[] = [];
  if (mailboxId !== undefined) menu.push({ id: "mail", label: t("mail.contacts.sendMail"), icon: "mail", onSelect: () => openComposer({ mailboxId, mode: "new", to: [{ address: c.email, ...(c.name === undefined ? {} : { name: c.name }) }], cc: [], subject: "", text: "" }) });
  if (c.userId === undefined) {
    menu.push({ id: "sep", separator: true });
    if (c.kind !== "client") menu.push({ id: "client", label: t("mail.contacts.markClient"), icon: "user", onSelect: () => vm.setContactKind(c.id, "client") });
    if (c.kind !== "team") menu.push({ id: "team", label: t("mail.contacts.markTeam"), icon: "users", onSelect: () => vm.setContactKind(c.id, "team") });
    if (c.kind !== "email") menu.push({ id: "email", label: t("mail.contacts.markEmail"), icon: "mail", onSelect: () => vm.setContactKind(c.id, "email") });
    menu.push({ id: "sep2", separator: true }, { id: "remove", label: t("mail.contacts.remove"), icon: "trash", destructive: true, onSelect: () => vm.remove(c.id) });
  }
  return (
    <Stack testID={`contact-${c.id}`}>
    <Pressable direction="horizontal" selected={selected} onPress={onOpen} accessibilityLabel={name} testID={`contact-open-${c.id}`}>
    <Stack grow direction="horizontal" align="center" gap="3" paddingX="6" paddingY="2">
      <Avatar name={name} src={Mail.mailAvatarPath(ws.vm.wsId, c.email)} size="avatar.md" />
      <Stack grow direction="horizontal" align="center" gap="3" wrap>
        <Stack width="property.label" gap="0.5">
          <Text weight="semibold" lines={1}>
            {name}
          </Text>
          {c.org === undefined ? null : (
            <Text variant="footnote" color="text.secondary" lines={1}>
              {c.org}
            </Text>
          )}
        </Stack>
        <Stack grow>
          <Text color="text.secondary" lines={1} selectable>
            {c.email}
          </Text>
        </Stack>
        <Stack direction="horizontal" align="center" gap="2" wrap>
          <Chip label={t(KIND_LABEL[c.kind])} tone={KIND_TONE[c.kind]} />
          {c.userId === undefined ? null : <Chip label={t("mail.contacts.member")} icon="user" />}
          <Text variant="footnote" color="text.tertiary">
            {t("mail.contacts.messages", { count: c.messageCount })}
          </Text>
        </Stack>
      </Stack>
      {canChange && menu.length > 0 ? (
        <Menu items={menu} align="end" testID={`contact-menu-${c.id}`}>
          <IconButton icon="more" accessibilityLabel={t("mail.more")} onPress={() => undefined} testID={`contact-more-${c.id}`} />
        </Menu>
      ) : null}
    </Stack>
    </Pressable>
    <Divider />
    </Stack>
  );
}

function AddContactSheet({ vm, open, onClose, initialKind }: { vm: ContactsViewModel; open: boolean; onClose: () => void; initialKind: ContactKind }) {
  const t = useT();
  const tr = useTr();
  const s = useViewModel(vm);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ContactKind>(initialKind);
  const save = async () => {
    if ((await vm.add(email.trim(), name, kind)) !== undefined) {
      setEmail("");
      setName("");
      onClose();
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("mail.contacts.add")} closeLabel={t("common.close")} testID="contacts-add-sheet">
      <Stack gap="3" padding="4">
        <TextField label={t("mail.contacts.email")} value={email} onChange={setEmail} keyboard="email" autoFocus onSubmit={() => void save()} testID="contacts-add-email" />
        <TextField label={t("mail.contacts.name")} value={name} onChange={setName} onSubmit={() => void save()} testID="contacts-add-name" />
        <Select<ContactKind>
          label={t("mail.contacts.kind")}
          value={kind}
          onChange={setKind}
          options={(["email", "client", "team"] as const).map((k) => ({ value: k, label: t(KIND_LABEL[k]) }))}
          testID="contacts-add-kind"
        />
        {s.error === undefined ? null : (
          <Text color="status.danger.fg" variant="footnote">
            {tr(s.error)}
          </Text>
        )}
        <Stack direction="horizontal" justify="end" gap="2">
          <Button variant="secondary" label={t("common.cancel")} onPress={onClose} testID="contacts-add-cancel" />
          <Button label={t("mail.contacts.add")} onPress={() => void save()} disabled={email.trim() === ""} testID="contacts-add-save" />
        </Stack>
      </Stack>
    </Sheet>
  );
}
